import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KoobButtonMenu, KoobMenuItem, KoobPageHeader } from '@koob/ui';
import KoobLabeledIcon from '@koob/ui/src/components/KoobLabeledIcon';
import { ExperienceTypeEnum } from '@koob/enums';
import ExperienceTypeIcon from './ExperienceTypeIcon';
import ExperienceExportModal from './ExperienceExportModal';

const positionName = (position) =>
  typeof position === 'string' ? position : position?.name;

// An activity is an extra unless it says otherwise.
const displayType = (experience) =>
  experience?.type === ExperienceTypeEnum.ACTIVITY &&
  experience?.activity?.isExtra !== false
    ? 'Extra'
    : experience?.type;

const TYPE_LABELS = {
  Activity: 'types.activity',
  Extra: 'types.extra',
  Program: 'types.program',
  Transfer: 'types.transfer',
};

/**
 * The header of every experience page: the record (`/experiences/:id` and its
 * tabs) or the creation wizard (`/experiences/new`, no id in the URL).
 * `experience` is the saved record, `undefined` while it loads.
 */
export default function ExperienceHeader({
  experience,
  actions,
  currentLocale,
}) {
  const { t } = useTranslation('experiences');
  const { experienceId } = useParams();
  const [exportAs, setExportAs] = useState(null);

  const breadcrumbs = [{ label: t('title'), to: '/experiences' }];

  if (!experienceId) {
    return (
      <KoobPageHeader
        title={t('create')}
        subtitle={t('creationSubtitle')}
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
    );
  }

  const path = `/experiences/${experienceId}`;
  const type = displayType(experience);
  const startPosition = positionName(experience?.startPosition);
  const endPosition = positionName(experience?.endPosition);
  const isProgram = experience?.type === ExperienceTypeEnum.PROGRAM;

  return (
    <>
      <KoobPageHeader
        title={experience?.name || undefined}
        id={experienceId}
        subtitle={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {TYPE_LABELS[type] && (
              <span className="inline-flex items-center gap-1.5">
                <ExperienceTypeIcon
                  type={type}
                  sizeClass="h-4 w-4 fill-current"
                />
                {t(TYPE_LABELS[type])}
              </span>
            )}
            {startPosition && (
              <span className="inline-flex items-center gap-x-2">
                <KoobLabeledIcon
                  icon="far fa-location-dot"
                  label={startPosition}
                />
                {endPosition && (
                  <>
                    <i className="far fa-chevron-right text-xs text-gray-400" />
                    <KoobLabeledIcon
                      icon="far fa-location-dot"
                      label={endPosition}
                    />
                  </>
                )}
              </span>
            )}
          </div>
        }
        breadcrumbs={breadcrumbs}
        actions={
          <>
            {actions}
            {experience?.name && (
              <KoobButtonMenu
                label={t('export.label')}
                icon="fa-regular fa-file-arrow-down"
                testId="experience-export"
                menu={
                  <>
                    <KoobMenuItem
                      icon="fa-regular fa-file-word"
                      onClick={() => setExportAs('docx')}
                    >
                      {t('export.docx')}
                    </KoobMenuItem>
                    <KoobMenuItem
                      icon="fa-regular fa-file-pdf"
                      onClick={() => setExportAs('pdf')}
                    >
                      {t('export.pdf')}
                    </KoobMenuItem>
                  </>
                }
              />
            )}
          </>
        }
        tabs={[
          { label: t('experienceTab'), to: path, end: true },
          { label: t('promotions'), to: `${path}/promotions` },
          ...(isProgram
            ? [
                {
                  label: t('compatibility.tabTitle'),
                  to: `${path}/compatibility`,
                },
                { label: t('recommendation'), to: `${path}/recommendation` },
              ]
            : []),
          { label: t('history.tab'), to: `${path}/history` },
        ]}
      />

      {experience?.name && (
        <ExperienceExportModal
          open={Boolean(exportAs)}
          close={() => setExportAs(null)}
          experienceId={experienceId}
          experienceName={experience.name}
          exportFormat={exportAs}
          currentLocale={currentLocale}
        />
      )}
    </>
  );
}
